import { Text, View, Image, Link, StyleSheet } from '@react-pdf/renderer';
import { sharedStyles, pdfColors, isAccessibleUrl } from './sharedStyles';
import { usePdfMediaResolver, type RemoteImageSrc } from './usePdfMediaResolver';
import type { AssessmentReportQuestionContentItem } from './types';

function toUri(src: string | RemoteImageSrc): string {
  return typeof src === 'string' ? src : src.uri;
}

const styles = StyleSheet.create({
  paragraph: {
    fontSize: 10,
    color: pdfColors.textLight,
    marginBottom: 8,
    lineHeight: 1.4,
  },
  heading: {
    fontSize: 12,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
    marginBottom: 8,
  },
  list: {
    marginBottom: 8,
    paddingLeft: 12,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  listItemBullet: {
    fontSize: 10,
    color: pdfColors.textLight,
    marginRight: 6,
    width: 15,
  },
  listItemText: {
    fontSize: 10,
    color: pdfColors.textLight,
    lineHeight: 1.4,
    flex: 1,
  },
  horizontalRule: {
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.border,
    marginVertical: 8,
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: pdfColors.borderMuted,
    paddingLeft: 12,
    marginVertical: 8,
    fontStyle: 'italic',
  },
  imageContainer: {
    marginVertical: 8,
    alignItems: 'center',
  },
  image: {
    maxWidth: 500,
    maxHeight: 400,
    borderRadius: 4,
  },
  pdfAttachment: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginVertical: 8,
    backgroundColor: pdfColors.background,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: pdfColors.border,
  },
  pdfIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
    backgroundColor: pdfColors.error,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pdfIconText: {
    fontSize: 8,
    color: pdfColors.white,
    fontFamily: 'Inter-SemiBold',
  },
  pdfTitle: {
    fontSize: 10,
    color: pdfColors.text,
    flex: 1,
  },
  pdfLink: {
    fontSize: 9,
    color: pdfColors.link,
    textDecoration: 'underline',
  },
  strong: {
    fontFamily: 'Inter-SemiBold',
  },
  italic: {
    fontStyle: 'italic',
  },
});

interface ContentRendererProps {
  content: AssessmentReportQuestionContentItem[];
}

function ContentRenderer({ content }: ContentRendererProps): React.ReactElement {
  const resolveMediaSrc = usePdfMediaResolver();

  return (
    <>
      {content.map((contentItem, idx) => {
        const itemKey = `${contentItem.type}-${idx}`;

        switch (contentItem.type) {
          case 'paragraph': {
            if (contentItem.content == null || typeof contentItem.content !== 'string') {
              return null;
            }

            return (
              <Text key={itemKey} style={styles.paragraph}>
                {contentItem.content}
              </Text>
            );
          }

          case 'heading': {
            if (contentItem.content == null || typeof contentItem.content !== 'string') {
              return null;
            }

            return (
              <Text key={itemKey} style={styles.heading}>
                {contentItem.content}
              </Text>
            );
          }

          case 'bulletList': {
            const items = contentItem.items;
            if (!items || items.length === 0) {
              return null;
            }
            return (
              <View key={itemKey} style={styles.list}>
                {items.map((item, i) => {
                  const listItemKey = `${itemKey}-item-${i}`;
                  return (
                    <View key={listItemKey} style={styles.listItem}>
                      <Text style={styles.listItemBullet}>•</Text>
                      <Text style={styles.listItemText}>{item}</Text>
                    </View>
                  );
                })}
              </View>
            );
          }

          case 'orderedList': {
            const items = contentItem.items;
            if (!items || items.length === 0) {
              return null;
            }
            return (
              <View key={itemKey} style={styles.list}>
                {items.map((item, i) => {
                  const listItemKey = `${itemKey}-item-${i}`;
                  return (
                    <View key={listItemKey} style={styles.listItem}>
                      <Text style={styles.listItemBullet}>{i + 1}.</Text>
                      <Text style={styles.listItemText}>{item}</Text>
                    </View>
                  );
                })}
              </View>
            );
          }

          case 'horizontalRule':
            return <View key={itemKey} style={styles.horizontalRule} />;

          case 'blockquote': {
            if (contentItem.content == null || typeof contentItem.content !== 'string') {
              return null;
            }

            return (
              <View key={itemKey} style={styles.blockquote}>
                <Text>{contentItem.content}</Text>
              </View>
            );
          }

          case 'image': {
            const src =
              contentItem.src != null
                ? resolveMediaSrc(contentItem.fileDrn, contentItem.src)
                : null;
            if (src != null && isAccessibleUrl(toUri(src))) {
              return (
                <View key={itemKey} style={styles.imageContainer}>
                  <Image
                    src={src}
                    style={
                      contentItem.width != null
                        ? [styles.image, { width: contentItem.width }]
                        : styles.image
                    }
                    cache={false}
                  />
                </View>
              );
            }

            return (
              <View key={itemKey} style={sharedStyles.mediaPlaceholder}>
                <Text style={sharedStyles.mediaPlaceholderText}>
                  Image preview unavailable in development environment.
                </Text>
              </View>
            );
          }

          case 'pdf': {
            const href =
              contentItem.href != null
                ? toUri(resolveMediaSrc(contentItem.fileDrn, contentItem.href))
                : null;
            const title = contentItem.title ?? 'Document';

            if (href != null && isAccessibleUrl(href)) {
              return (
                <View key={itemKey} style={styles.pdfAttachment}>
                  <View style={styles.pdfIcon}>
                    <Text style={styles.pdfIconText}>PDF</Text>
                  </View>
                  <Text style={styles.pdfTitle}>{title}</Text>
                  <Link src={href} style={styles.pdfLink}>
                    Download
                  </Link>
                </View>
              );
            }

            return (
              <View key={itemKey} style={sharedStyles.mediaPlaceholder}>
                <Text style={sharedStyles.mediaPlaceholderText}>
                  PDF preview unavailable in development environment.
                </Text>
              </View>
            );
          }

          case 'text': {
            if (contentItem.content == null || typeof contentItem.content !== 'string') {
              return null;
            }

            return (
              <Text key={itemKey} style={sharedStyles.contentText}>
                {contentItem.content}
              </Text>
            );
          }

          default:
            return (
              <View key={itemKey} style={sharedStyles.mediaPlaceholder}>
                <Text style={sharedStyles.mediaPlaceholderText}>
                  [Unhandled type: {contentItem.type}]
                </Text>
              </View>
            );
        }
      })}
    </>
  );
}

export default ContentRenderer;
